import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import "../Dashboard.css";

const numberFormat = new Intl.NumberFormat();

function AdminDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("dashboard/summary/");
      setSummary(response.data || {});
    } catch (error) {
      console.error(error);
      toast.error("Failed to load dashboard summary.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const people = summary?.people || {};
  const academic = summary?.academic || {};
  const claims = summary?.parent_claims || {};
  const security = summary?.security || {};
  const recentUsers = summary?.recent_users || [];

  const stats = useMemo(
    () => [
      {
        title: "Students",
        value: people.students || 0,
        icon: "bi-mortarboard",
        color: "#125b2d",
        helper: "Registered learners",
        action: () => navigate("/students"),
      },
      {
        title: "Teachers",
        value: people.teachers || 0,
        icon: "bi-person-workspace",
        color: "#144e76",
        helper: `${people.pending_teachers || 0} pending approval`,
        action: () => navigate("/teachers"),
      },
      {
        title: "Parents",
        value: people.parents || 0,
        icon: "bi-people",
        color: "#6d28d9",
        helper: `${claims.pending || 0} student claims pending`,
        action: () => navigate("/parents"),
      },
      {
        title: "Active Users",
        value: people.active_users || 0,
        icon: "bi-person-check",
        color: "#0f766e",
        helper: `${people.inactive_users || 0} inactive accounts`,
        action: () => navigate("/settings"),
      },
    ],
    [claims.pending, navigate, people]
  );

  const setupItems = [
    { label: "Academic Year", ready: Boolean(academic.active_year), value: academic.active_year || "Not set" },
    { label: "Active Term", ready: Boolean(academic.active_term), value: academic.active_term || "Not set" },
    { label: "Class Groups", ready: (academic.class_groups || 0) > 0, value: academic.class_groups || 0 },
    { label: "Subjects", ready: (academic.subjects || 0) > 0, value: academic.subjects || 0 },
    { label: "Class Subjects", ready: (academic.class_subjects || 0) > 0, value: academic.class_subjects || 0 },
    { label: "Teacher Allocations", ready: (academic.teacher_allocations || 0) > 0, value: academic.teacher_allocations || 0 },
  ];

  const setupScore = Math.round(
    (setupItems.filter((item) => item.ready).length / setupItems.length) * 100
  );

  const moduleBars = [
    { label: "Users", value: people.active_users || 0, color: "#125b2d" },
    { label: "Academic", value: academic.class_subjects || 0, color: "#144e76" },
    { label: "Approvals", value: (claims.pending || 0) + (people.pending_teachers || 0), color: "#b45309" },
    { label: "Roles", value: security.roles || 0, color: "#6d28d9" },
  ];

  const maxBarValue = Math.max(...moduleBars.map((item) => item.value), 1);

  const workQueue = [
    {
      title: "Pending Parent Claims",
      count: claims.pending || 0,
      icon: "bi-link-45deg",
      tone: "warning",
      action: () => navigate("/parents"),
    },
    {
      title: "Pending Teachers",
      count: people.pending_teachers || 0,
      icon: "bi-person-plus",
      tone: "info",
      action: () => navigate("/teachers"),
    },
    {
      title: "Users Without Roles",
      count: security.users_without_roles || 0,
      icon: "bi-shield-exclamation",
      tone: "danger",
      action: () => navigate("/settings"),
    },
  ];

  if (loading && !summary) {
    return (
      <div className="dashboard-loading">
        <span className="spinner-border text-success"></span>
        <span className="fw-semibold">Loading live dashboard...</span>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-hero mb-4">
        <div>
          <span className="dashboard-kicker">Admin Command Center</span>
          <h3 className="fw-bold mb-2">The Grange Christian School Dashboard</h3>
          <p className="mb-0 text-muted">
            Live overview of people, academic setup, approvals, and system readiness.
          </p>
        </div>

        <div className="d-flex flex-wrap gap-2">
          <button className="btn btn-light border fw-semibold" onClick={loadDashboard}>
            <i className="bi bi-arrow-clockwise me-2"></i>
            Refresh
          </button>
          <button className="btn btn-success fw-semibold" onClick={() => navigate("/settings")}>
            <i className="bi bi-gear me-2"></i>
            Settings
          </button>
        </div>
      </div>

      <div className="dashboard-live-strip mb-4">
        <i className="bi bi-activity me-2"></i>
        Live system pulse: {people.active_users || 0} active users,{" "}
        {claims.pending || 0} parent claims, {academic.teacher_allocations || 0} teacher allocations.
      </div>

      <div className="row g-3 mb-4">
        {stats.map((item) => (
          <div className="col-md-6 col-xl-3" key={item.title}>
            <button className="dashboard-stat-card text-start" onClick={item.action}>
              <div className="d-flex align-items-start justify-content-between gap-3">
                <div>
                  <small className="text-muted fw-bold">{item.title}</small>
                  <h2 className="fw-bold mt-2 mb-1">
                    {numberFormat.format(item.value)}
                  </h2>
                  <span className="small text-muted">{item.helper}</span>
                </div>
                <span className="dashboard-stat-icon" style={{ background: item.color }}>
                  <i className={`bi ${item.icon}`}></i>
                </span>
              </div>
            </button>
          </div>
        ))}
      </div>

      <div className="row g-4">
        <div className="col-xl-8">
          <div className="row g-4 mb-4">
            <div className="col-lg-7">
              <div className="card border-0 shadow-sm dashboard-chart-card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h5 className="fw-bold mb-1">Platform Growth</h5>
                      <p className="text-muted mb-0">Current population mix and setup activity.</p>
                    </div>
                    <span className="badge bg-success-subtle text-success border border-success-subtle">
                      Live
                    </span>
                  </div>

                  <svg className="dashboard-line-chart" viewBox="0 0 520 170" role="img">
                    <defs>
                      <linearGradient id="dashboardLineFill" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#125b2d" stopOpacity="0.22" />
                        <stop offset="100%" stopColor="#125b2d" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M20 145 L110 118 L200 126 L290 78 L380 92 L500 42 L500 160 L20 160 Z"
                      fill="url(#dashboardLineFill)"
                    />
                    <path
                      className="line"
                      d="M20 145 L110 118 L200 126 L290 78 L380 92 L500 42"
                      fill="none"
                      stroke="#125b2d"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {[20, 110, 200, 290, 380, 500].map((x, index) => {
                      const yValues = [145, 118, 126, 78, 92, 42];
                      return (
                        <circle
                          key={x}
                          cx={x}
                          cy={yValues[index]}
                          r="6"
                          fill="#fff"
                          stroke="#125b2d"
                          strokeWidth="4"
                        />
                      );
                    })}
                  </svg>
                </div>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="card border-0 shadow-sm dashboard-chart-card">
                <div className="card-body">
                  <h5 className="fw-bold mb-3">Module Load</h5>
                  {moduleBars.map((item) => (
                    <div className="dashboard-bar" key={item.label}>
                      <span className="fw-semibold">{item.label}</span>
                      <span className="dashboard-bar-track">
                        <span
                          className="dashboard-bar-fill"
                          style={{
                            width: `${Math.max((item.value / maxBarValue) * 100, item.value ? 14 : 3)}%`,
                            background: item.color,
                          }}
                        ></span>
                      </span>
                      <strong>{numberFormat.format(item.value)}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
                <div>
                  <h5 className="fw-bold mb-1">Academic Setup Health</h5>
                  <p className="text-muted mb-0">
                    Build this foundation before teacher dashboards and e-learning.
                  </p>
                </div>
                <div
                  className="dashboard-score"
                  style={{ "--score": `${setupScore}%` }}
                >
                  <span>{setupScore}%</span>
                  <small>ready</small>
                </div>
              </div>

              <div className="progress dashboard-progress mb-3">
                <div
                  className="progress-bar bg-success"
                  style={{ width: `${setupScore}%` }}
                ></div>
              </div>

              <div className="row g-3">
                {setupItems.map((item) => (
                  <div className="col-md-6" key={item.label}>
                    <div className="dashboard-setup-item">
                      <span
                        className={`dashboard-dot ${
                          item.ready ? "is-ready" : "is-missing"
                        }`}
                      ></span>
                      <div>
                        <div className="fw-bold">{item.label}</div>
                        <small className="text-muted">{item.value}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                className="btn btn-outline-success fw-semibold mt-3"
                onClick={() => navigate("/settings")}
              >
                Open Academic Setup
              </button>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Recent Accounts</h5>

              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Role</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentUsers.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-4">
                          No accounts have been created yet.
                        </td>
                      </tr>
                    ) : (
                      recentUsers.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="fw-bold">{user.full_name}</div>
                            <small className="text-muted">{user.email}</small>
                          </td>
                          <td>{formatLabel(user.user_type)}</td>
                          <td>{user.role || "No role"}</td>
                          <td>
                            <span
                              className={`badge ${
                                user.is_active ? "bg-success" : "bg-secondary"
                              }`}
                            >
                              {user.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-4">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Work Queue</h5>
              {workQueue.map((item) => (
                <button
                  key={item.title}
                  className="dashboard-queue-item"
                  onClick={item.action}
                >
                  <span className={`dashboard-queue-icon text-${item.tone}`}>
                    <i className={`bi ${item.icon}`}></i>
                  </span>
                  <span className="flex-grow-1">
                    <span className="fw-bold d-block">{item.title}</span>
                    <small className="text-muted">Requires admin attention</small>
                  </span>
                  <strong>{numberFormat.format(item.count)}</strong>
                </button>
              ))}
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Quick Actions</h5>
              <div className="d-grid gap-2">
                <button className="btn btn-success fw-semibold" onClick={() => navigate("/students")}>
                  <i className="bi bi-person-plus me-2"></i>
                  Register Student
                </button>
                <button className="btn btn-light border fw-semibold" onClick={() => navigate("/teachers")}>
                  <i className="bi bi-person-workspace me-2"></i>
                  Manage Teachers
                </button>
                <button className="btn btn-light border fw-semibold" onClick={() => navigate("/parents")}>
                  <i className="bi bi-people me-2"></i>
                  Review Parents
                </button>
                <button className="btn btn-light border fw-semibold" onClick={() => navigate("/settings")}>
                  <i className="bi bi-mortarboard me-2"></i>
                  Academic Setup
                </button>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="fw-bold mb-3">System Snapshot</h5>
              <SnapshotRow label="Roles" value={security.roles || 0} />
              <SnapshotRow label="System Users" value={people.system_users || 0} />
              <SnapshotRow label="Pending Users" value={people.pending_users || 0} />
              <SnapshotRow label="Parent Claims Approved" value={claims.approved || 0} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SnapshotRow({ label, value }) {
  return (
    <div className="d-flex justify-content-between align-items-center border-bottom py-2">
      <span className="text-muted">{label}</span>
      <strong>{numberFormat.format(value)}</strong>
    </div>
  );
}

function formatLabel(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default AdminDashboard;
